import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { FLIGHT_ALLOTMENT_STATUS_LABELS, type FlightAllotmentStatus } from "@ckgo2/shared/types";
import { formatDate } from "@/lib/format";
import { Plus, Plane, ClipboardList, AlertTriangle } from "lucide-react";
import i18n, { uiLocale } from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";
import MonitoredFlightsSection from "./MonitoredFlightsSection";

/** One enriched allotment row from GET /api/admin/flight-allotments. */
interface AllotmentRow {
  id: string;
  airline: string;
  label: string;
  pnr?: string | null;
  status: FlightAllotmentStatus;
  currency: string;
  seatsTotal: number;
  pricePerSeat?: number | null;
  used: number;
  remaining: number;
  atRisk: number | null;
  nextDeadline: "deposit" | "foc" | "ticketing" | null;
  dueDate: string | null;
  daysToNext: number | null;
  actionText: string;
  severity: "ok" | "warn" | "urgent";
  departureCode?: string | null;
  departureDate?: string | null;
  tripTitle?: string | null;
  assignedToName?: string | null;
}

interface AtRiskSummary {
  amountByCurrency: Record<string, number>;
  blocks: number;
}

const DEADLINE_LABEL: Record<"deposit" | "foc" | "ticketing", string> = {
  deposit: i18n.t("ops.flightDeadlines.deposit"),
  foc: i18n.t("ops.flightDeadlines.foc"),
  ticketing: i18n.t("ops.flightDeadlines.ticketing"),
};

const STATUS_BADGE: Record<FlightAllotmentStatus, string> = {
  active: "bg-emerald-100 text-emerald-700",
  released: "bg-gray-100 text-gray-600",
  ticketed: "bg-purple-100 text-purple-700",
  cancelled: "bg-red-100 text-red-600",
};

const SEVERITY_CHIP: Record<"ok" | "warn" | "urgent", string> = {
  ok: "bg-gray-100 text-gray-600",
  warn: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

function money(n: number, cur: string): string {
  return `${n.toLocaleString(uiLocale())} ${cur}`;
}

function countdown(days: number | null): string {
  if (days == null) return "—";
  if (days < 0) return i18n.t("ops.countdown.overdue", { days: Math.abs(days) });
  if (days === 0) return i18n.t("ops.countdown.today");
  return i18n.t("ops.countdown.inDays", { days });
}

export default function FlightDeskPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["flight-allotments"],
    queryFn: () => api.get<ApiResponse<AllotmentRow[]>>("/api/admin/flight-allotments"),
  });
  const { data: risk } = useQuery({
    queryKey: ["flight-allotments-at-risk"],
    queryFn: () =>
      api.get<ApiResponse<AtRiskSummary>>("/api/admin/flight-allotments/at-risk-summary"),
  });

  const rows = data?.data ?? [];
  const riskTotal = risk?.data;
  const riskParts = riskTotal
    ? Object.entries(riskTotal.amountByCurrency).map(([cur, amt]) => money(amt, cur))
    : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <Plane className="h-6 w-6" /> {t("ops.flightDesk.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("ops.flightDesk.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/services?kind=flight&sort=deadline"
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300"
          >
            <ClipboardList className="h-4 w-4" /> {t("ops.flightDesk.flightQueue")}
          </Link>
          <Link
            to="/flights/new"
            className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          >
            <Plus className="h-4 w-4" /> {t("ops.flightDesk.newAllotment")}
          </Link>
        </div>
      </div>

      {/* Owner headline: money at risk on unsold seats this week. */}
      {riskParts.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-900/20">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="text-sm text-red-800 dark:text-red-200">
            {t("ops.flightDesk.atRiskPrefix")} <strong>{riskParts.join(" + ")}</strong>{" "}
            {t("ops.flightDesk.atRiskSuffix", { count: riskTotal?.blocks ?? 0 })}
          </p>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        {isLoading ? (
          <p className="p-6 text-gray-500">{t("common.states.loading")}</p>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Plane className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-gray-500">{t("ops.flightDesk.empty")}</p>
            <Link
              to="/flights/new"
              className="text-primary-600 mt-1 inline-block text-sm hover:underline"
            >
              {t("ops.flightDesk.createFirst")}
            </Link>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:bg-gray-900/40">
              <tr>
                <th className="px-4 py-3">{t("ops.flightDesk.colDeparture")}</th>
                <th className="px-4 py-3">{t("ops.flightDesk.colAirlineRoute")}</th>
                <th className="px-4 py-3">{t("ops.flightDesk.colSold")}</th>
                <th className="px-4 py-3">{t("ops.flightDesk.colNextDeadline")}</th>
                <th className="px-4 py-3">{t("ops.flightDesk.colAction")}</th>
                <th className="px-4 py-3">{t("ops.flightDesk.colStatus")}</th>
                <th className="px-4 py-3">{t("ops.flightDesk.colOwner")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {rows.map((r) => {
                const pct = r.seatsTotal > 0 ? Math.round((r.used / r.seatsTotal) * 100) : 0;
                return (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <Link
                        to={`/flights/${r.id}`}
                        className="text-primary-600 font-medium hover:underline"
                      >
                        {r.departureCode || "—"}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {r.departureDate ? formatDate(r.departureDate) : r.tripTitle || ""}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-white">{r.airline}</div>
                      <div className="text-xs text-gray-500">{r.label}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700 dark:text-gray-200">
                        {r.used}/{r.seatsTotal}
                      </div>
                      <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className="bg-primary-500 h-full"
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.nextDeadline ? (
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_CHIP[r.severity]}`}
                        >
                          {DEADLINE_LABEL[r.nextDeadline]} · {countdown(r.daysToNext)}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {r.dueDate && (
                        <div className="mt-0.5 text-xs text-gray-400">{formatDate(r.dueDate)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      {r.actionText || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[r.status]}`}
                      >
                        {enumLabel(FLIGHT_ALLOTMENT_STATUS_LABELS, r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{r.assignedToName || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <MonitoredFlightsSection />
    </div>
  );
}
